/**
 * TemplateManager — download, check, preview and apply the base roster template.
 *
 * Upload is a four-stage flow rather than a single blind commit:
 *
 *   idle → checking → review → applying → idle
 *
 * "review" is the important one. It shows every problem found in the workbook at
 * once (with the cell address and a suggested fix), and a diff of what applying
 * the file would actually change against the live config. Nothing is written
 * until the admin confirms, and a snapshot taken at that moment makes it undoable.
 */

import { useState, useRef, useEffect, useCallback } from "react";
import { apiClient } from "@/api/client";
import { useNotification } from "@/contexts/NotificationContext";
import { Button } from "./Button";
import { readPickedFile } from "@/utils/fileInput";
import type {
  TemplateValidateResponse,
  TemplateFinding,
  TemplateDiffTab,
  ConfigDiffTab,
  TemplateSnapshot,
  TemplateExportSource,
} from "@/types/api";

type Stage = "idle" | "checking" | "review" | "applying";

/**
 * "Download the template" means three different things depending on the task, so
 * the choice is explicit rather than a default with a hidden alternative.
 */
const DOWNLOADS: { source: TemplateExportSource; label: string; filename: string }[] = [
  { source: "live", label: "Current", filename: "roster_config.xlsx" },
  { source: "defaults", label: "Default", filename: "roster_config_example.xlsx" },
  { source: "blank", label: "Blank", filename: "roster_config_blank.xlsx" },
];

const COLORS = {
  error: { fg: "#B91C1C", bg: "#FEF2F2", border: "#FECACA" },
  warning: { fg: "#92400E", bg: "#FFFBEB", border: "#FDE68A" },
  ok: { fg: "#065F46", bg: "#ECFDF5", border: "#6EE7B7" },
} as const;

function formatWhen(iso: string | null): string {
  if (!iso) return "unknown time";
  const d = new Date(iso.endsWith("Z") ? iso : `${iso}Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
  });
}

// ─── Findings list ────────────────────────────────────────────────────────────

function FindingRow({ finding }: { finding: TemplateFinding }) {
  const c = COLORS[finding.severity];
  const where = [finding.sheet, finding.cell].filter(Boolean).join("!");
  return (
    <li
      style={{
        display: "flex",
        gap: 8,
        padding: "7px 10px",
        borderTop: `1px solid ${c.border}`,
        alignItems: "flex-start",
        lineHeight: 1.5,
      }}
    >
      <span style={{ fontSize: 11, flexShrink: 0, marginTop: 1 }} aria-hidden>
        {finding.severity === "error" ? "⛔" : "⚠️"}
      </span>
      <div style={{ minWidth: 0 }}>
        {where && (
          <code
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: c.fg,
              background: "rgba(0,0,0,0.04)",
              padding: "1px 5px",
              borderRadius: 3,
              marginRight: 6,
            }}
          >
            {where}
          </code>
        )}
        <span style={{ fontSize: 12, color: "#374151" }}>{finding.message}</span>
        {finding.suggestion && (
          <div style={{ fontSize: 11, color: c.fg, marginTop: 2 }}>{finding.suggestion}</div>
        )}
      </div>
    </li>
  );
}

function FindingsPanel({ findings }: { findings: TemplateFinding[] }) {
  const errors = findings.filter((f) => f.severity === "error");
  const warnings = findings.filter((f) => f.severity === "warning");
  if (findings.length === 0) return null;

  const groups: { title: string; items: TemplateFinding[]; tone: keyof typeof COLORS }[] = [
    { title: `${errors.length} problem${errors.length === 1 ? "" : "s"} to fix`, items: errors, tone: "error" as const },
    { title: `${warnings.length} thing${warnings.length === 1 ? "" : "s"} to check`, items: warnings, tone: "warning" as const },
  ].filter((g) => g.items.length > 0);

  return (
    <div style={{ marginBottom: 14 }}>
      {groups.map((g) => {
        const c = COLORS[g.tone];
        return (
          <div
            key={g.tone}
            style={{
              background: c.bg,
              border: `1px solid ${c.border}`,
              borderRadius: 8,
              marginBottom: 10,
              overflow: "hidden",
            }}
          >
            <div style={{ padding: "8px 10px", fontSize: 12, fontWeight: 700, color: c.fg }}>
              {g.title}
              {g.tone === "warning" && (
                <span style={{ fontWeight: 400, marginLeft: 6 }}>— you can still apply</span>
              )}
            </div>
            <ul style={{ margin: 0, padding: 0, listStyle: "none", maxHeight: 260, overflowY: "auto" }}>
              {g.items.map((f, i) => <FindingRow key={i} finding={f} />)}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

// ─── Diff preview ─────────────────────────────────────────────────────────────

function CounterChanges({ counters }: { counters: TemplateDiffTab["counters"] }) {
  if (!counters.changed) return null;
  return (
    <div style={{ marginTop: 6, fontSize: 11, color: "#6B7280" }}>
      {counters.renamed.map((r) => (
        <div key={`r${r.index}`}>
          Renamed <strong>{r.from}</strong> → <strong>{r.to}</strong>
        </div>
      ))}
      {counters.motor_changed.map((m) => (
        <div key={`m${m.label}`}>
          <strong>{m.label}</strong> is now {m.to ? "a motor counter" : "a car counter"}
        </div>
      ))}
      {counters.added.length > 0 && <div>Added counters: {counters.added.join(", ")}</div>}
      {counters.removed.length > 0 && <div>Removed counters: {counters.removed.join(", ")}</div>}
    </div>
  );
}

function DiffTab({ tab }: { tab: TemplateDiffTab }) {
  const [expanded, setExpanded] = useState(false);

  if (tab.unchanged) {
    return (
      <div style={{ padding: "8px 10px", fontSize: 12, color: "#9CA3AF", borderTop: "1px solid #E5E7EB" }}>
        <strong style={{ color: "#6B7280" }}>{tab.sheet}</strong> — no change
      </div>
    );
  }

  const headline: string[] = [];
  if (tab.changed_officer_count > 0) {
    headline.push(`${tab.changed_officer_count} officer${tab.changed_officer_count === 1 ? "" : "s"} changed`);
  }
  if (tab.added_sns.length > 0) headline.push(`${tab.added_sns.length} added`);
  if (tab.removed_sns.length > 0) headline.push(`${tab.removed_sns.length} removed`);
  if (tab.counters.changed) headline.push("counters changed");

  return (
    <div style={{ borderTop: "1px solid #E5E7EB" }}>
      <button
        onClick={() => setExpanded((v) => !v)}
        style={{
          width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center",
          gap: 8, padding: "8px 10px", background: "none", border: "none", cursor: "pointer",
          textAlign: "left",
        }}
      >
        <span style={{ fontSize: 12, color: "#374151" }}>
          <strong>{tab.sheet}</strong>
          <span style={{ color: "#6B7280" }}> — {headline.join(", ")}</span>
        </span>
        <span style={{ fontSize: 10, color: "#9CA3AF", flexShrink: 0 }}>{expanded ? "▲" : "▼"}</span>
      </button>

      {expanded && (
        <div style={{ padding: "0 10px 10px 10px" }}>
          <div style={{ fontSize: 11, color: "#9CA3AF", marginBottom: 6 }}>
            Currently from {tab.source} · {tab.officers_before} → {tab.officers_after} officers
          </div>

          {tab.added_sns.length > 0 && (
            <div style={{ fontSize: 11, color: "#065F46", marginBottom: 4 }}>
              New: {tab.added_sns.map((s) => `SN${s}`).join(", ")}
            </div>
          )}
          {tab.removed_sns.length > 0 && (
            <div style={{ fontSize: 11, color: "#B91C1C", marginBottom: 4 }}>
              Removed: {tab.removed_sns.map((s) => `SN${s}`).join(", ")}
            </div>
          )}

          <CounterChanges counters={tab.counters} />

          {tab.changed_officers.length > 0 && (
            <div style={{ marginTop: 8, maxHeight: 220, overflowY: "auto" }}>
              {tab.changed_officers.map((o) => (
                <div key={o.sn} style={{ marginBottom: 6, fontSize: 11 }}>
                  <span style={{ fontWeight: 700, color: "#374151" }}>SN{o.sn}</span>
                  <span style={{ color: "#9CA3AF" }}> · {o.slots_changed} slot{o.slots_changed === 1 ? "" : "s"}</span>
                  {o.ranges.map((r, i) => (
                    <div key={i} style={{ color: "#6B7280", paddingLeft: 10 }}>
                      {r.from_time}–{r.to_time}: <span style={{ color: "#B91C1C" }}>{r.before}</span>
                      {" → "}
                      <span style={{ color: "#065F46" }}>{r.after}</span>
                    </div>
                  ))}
                </div>
              ))}
              {tab.changed_officer_count > tab.changed_officers.length && (
                <div style={{ fontSize: 11, color: "#9CA3AF" }}>
                  …and {tab.changed_officer_count - tab.changed_officers.length} more officer(s)
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ConfigDiffRow({ tab }: { tab: ConfigDiffTab }) {
  const [expanded, setExpanded] = useState(false);

  if (tab.unchanged) {
    return (
      <div style={{ padding: "8px 10px", fontSize: 12, color: "#9CA3AF", borderTop: "1px solid #E5E7EB" }}>
        <strong style={{ color: "#6B7280" }}>{tab.sheet}</strong> — no change
      </div>
    );
  }

  const added = tab.added_count ?? tab.added.length;
  const removed = tab.removed_count ?? tab.removed.length;

  return (
    <div style={{ borderTop: "1px solid #E5E7EB" }}>
      <button
        onClick={() => setExpanded((v) => !v)}
        style={{
          width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center",
          gap: 8, padding: "8px 10px", background: "none", border: "none", cursor: "pointer",
          textAlign: "left",
        }}
      >
        <span style={{ fontSize: 12, color: "#374151" }}>
          <strong>{tab.sheet}</strong>
          <span style={{ color: "#6B7280" }}>
            {" — "}
            {[added && `${added} added`, removed && `${removed} removed`].filter(Boolean).join(", ")
              || `${tab.rows_before} → ${tab.rows_after} rows`}
          </span>
        </span>
        <span style={{ fontSize: 10, color: "#9CA3AF", flexShrink: 0 }}>{expanded ? "▲" : "▼"}</span>
      </button>

      {expanded && (
        <div style={{ padding: "0 10px 10px", fontSize: 11, maxHeight: 200, overflowY: "auto" }}>
          {tab.removed.map((r, i) => (
            <div key={`r${i}`} style={{ color: "#B91C1C" }}>− {r}</div>
          ))}
          {tab.added.map((a, i) => (
            <div key={`a${i}`} style={{ color: "#065F46" }}>+ {a}</div>
          ))}
        </div>
      )}
    </div>
  );
}

function DiffPanel({ result }: { result: TemplateValidateResponse }) {
  const { diff } = result;
  const configTabs = diff?.config_tabs ?? [];
  if (!diff?.tabs?.length && configTabs.length === 0) return null;

  const changedTabs =
    diff.tabs.filter((t) => !t.unchanged).length + configTabs.filter((t) => !t.unchanged).length;

  return (
    <div style={{ border: "1px solid #E5E7EB", borderRadius: 8, marginBottom: 14, overflow: "hidden" }}>
      <div style={{ padding: "8px 10px", background: "#F9FAFB", fontSize: 12, fontWeight: 700, color: "#374151" }}>
        {diff.any_change
          ? `What will change — ${diff.total_changed_officers} officer${diff.total_changed_officers === 1 ? "" : "s"} across ${changedTabs} tab(s)`
          : "No changes — this file matches the current configuration"}
      </div>
      {diff.tabs.map((t) => <DiffTab key={t.sheet} tab={t} />)}
      {configTabs.length > 0 && (
        <div style={{ padding: "6px 10px", background: "#F9FAFB", borderTop: "1px solid #E5E7EB",
                      fontSize: 11, fontWeight: 700, color: "#6B7280" }}>
          Settings tabs
        </div>
      )}
      {configTabs.map((t) => <ConfigDiffRow key={t.sheet} tab={t} />)}
    </div>
  );
}

// ─── Rollback ─────────────────────────────────────────────────────────────────

function SnapshotList({
  snapshots,
  onRollback,
  busy,
}: {
  snapshots: TemplateSnapshot[];
  onRollback: (id: number) => void;
  busy: boolean;
}) {
  if (snapshots.length === 0) return null;
  return (
    <div style={{ marginTop: 14, borderTop: "1px solid #E5E7EB", paddingTop: 12 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 6 }}>
        Undo a previous change
      </div>
      {snapshots.map((s) => (
        <div
          key={s.id}
          style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            gap: 10, padding: "5px 0", fontSize: 12, color: "#6B7280",
          }}
        >
          <span style={{ minWidth: 0 }}>
            {formatWhen(s.created_at)} — {s.label}
            {s.created_by && <span style={{ color: "#9CA3AF" }}> ({s.created_by})</span>}
          </span>
          <button
            onClick={() => onRollback(s.id)}
            disabled={busy}
            style={{
              flexShrink: 0, padding: "3px 10px", fontSize: 11, fontWeight: 600,
              background: "white", border: "1px solid #D1D5DB", borderRadius: 5,
              color: "#374151", cursor: busy ? "not-allowed" : "pointer",
            }}
          >
            Restore
          </button>
        </div>
      ))}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

interface TemplateManagerProps {
  /**
   * Skip the outer card, title and intro text — for embedding inside a container
   * that already provides them (the merged Roster Template section in the admin
   * panel). Standalone callers (the generate wizard) leave this off.
   */
  bare?: boolean;
  /** Fires after apply, rollback or reset — anything that changes the live template. */
  onChanged?: () => void;
  /** Bump to make this refresh its own snapshot list — e.g. after a sibling grid-editor save. */
  refreshSignal?: number;
}

export function TemplateManager({ bare = false, onChanged, refreshSignal }: TemplateManagerProps = {}) {
  const { notifySuccess, notifyError } = useNotification();
  const [downloading, setDownloading] = useState(false);
  const [stage, setStage] = useState<Stage>("idle");
  const [resetting, setResetting] = useState(false);
  const [review, setReview] = useState<TemplateValidateResponse | null>(null);
  const [lastResult, setLastResult] = useState<string | null>(null);
  const [storageWarning, setStorageWarning] = useState<string | null>(null);
  const [snapshots, setSnapshots] = useState<TemplateSnapshot[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const lastPickedRef = useRef<File | null>(null);

  const refreshSnapshots = useCallback(() => {
    apiClient.getTemplateSnapshots()
      .then((r) => setSnapshots(r.snapshots))
      .catch(() => {/* rollback list is a convenience — never block on it */});
  }, []);

  useEffect(() => {
    apiClient.getTemplateStorageInfo().then(info => {
      if (!info.persistent) setStorageWarning(info.warning ?? null);
    }).catch(() => {/* ignore */});
    refreshSnapshots();
  }, [refreshSnapshots]);

  // A sibling grid editor saved a direct edit — our snapshot list is now stale.
  useEffect(() => {
    if (refreshSignal !== undefined) refreshSnapshots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshSignal]);

  async function handleDownload(source: TemplateExportSource) {
    setDownloading(true);
    try {
      const blob = await apiClient.exportTemplate(source);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = DOWNLOADS.find((d) => d.source === source)?.filename ?? "roster_config.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      notifyError(`Download failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setDownloading(false);
    }
  }

  async function handleReset() {
    if (!window.confirm(
      "Reset the roster template to the built-in defaults?\n\n" +
      "Any uploaded custom template will be removed. A snapshot is saved first, so this can be undone."
    )) return;
    setResetting(true);
    setLastResult(null);
    try {
      const result = await apiClient.resetTemplate();
      setLastResult(result.message);
      notifySuccess(result.message);
      refreshSnapshots();
      onChanged?.();
    } catch (err) {
      notifyError(`Reset failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setResetting(false);
    }
  }

  /** Stage 1: check the file. Writes nothing. */
  async function handleFileChange(e: React.SyntheticEvent<HTMLInputElement>) {
    const input = e.currentTarget;
    const file = await readPickedFile(input);
    if (!file || file === lastPickedRef.current) return;
    lastPickedRef.current = file;
    window.setTimeout(() => { lastPickedRef.current = null; }, 1000);
    if (!file.name.toLowerCase().endsWith(".xlsx")) {
      notifyError("Please select an .xlsx Excel file.");
      input.value = "";
      return;
    }
    setStage("checking");
    setLastResult(null);
    setReview(null);
    try {
      const result = await apiClient.validateTemplate(file);
      setReview(result);
      setStage("review");
    } catch (err) {
      notifyError(`Could not read the file: ${err instanceof Error ? err.message : String(err)}`);
      setStage("idle");
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  /** Stage 2: commit the reviewed file. */
  async function handleApply() {
    if (!review?.token) return;
    setStage("applying");
    try {
      const result = await apiClient.applyTemplate(review.token);
      setLastResult(result.message);
      notifySuccess(result.message);
      setReview(null);
      setStage("idle");
      refreshSnapshots();
      onChanged?.();
    } catch (err) {
      notifyError(`Apply failed: ${err instanceof Error ? err.message : String(err)}`);
      setStage("review");
    }
  }

  async function handleRollback(snapshotId: number) {
    if (!window.confirm("Restore this earlier version of the roster template?")) return;
    setStage("applying");
    try {
      const result = await apiClient.rollbackTemplate(snapshotId);
      setLastResult(result.message);
      notifySuccess(result.message);
      refreshSnapshots();
      onChanged?.();
    } catch (err) {
      notifyError(`Restore failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setStage("idle");
    }
  }

  const busy = stage === "checking" || stage === "applying";

  return (
    <div
      style={
        bare
          ? undefined
          : { background: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: 10, padding: "20px 24px" }
      }
    >
      {!bare && (
        <>
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4, color: "#111827" }}>
            Roster Template
          </div>
          <div style={{ fontSize: 13, color: "#6B7280", marginBottom: 12, lineHeight: 1.5 }}>
            The template defines which counters each main officer works across all 48 time
            slots. Download as Excel, edit the counter assignments, then upload back — no code
            required. Uploads are checked and previewed before anything is saved.
          </div>

          <div
            style={{
              display: "flex", gap: 8, alignItems: "flex-start",
              background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 8,
              padding: "9px 11px", marginBottom: 16, fontSize: 12, color: "#1E3A5F", lineHeight: 1.6,
            }}
          >
            <span aria-hidden>ℹ️</span>
            <div>
              Changes here apply to <strong>rosters generated from now on</strong>. Any roster
              already generated keeps the template it was built with, and nothing here appears
              in that roster's version history until you generate a new one.
            </div>
          </div>
        </>
      )}

      {/* Download — an explicit choice, because "the template" means three
          different things depending on what you are about to do. */}
      <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
        {DOWNLOADS.map((d) => (
          <button
            key={d.source}
            onClick={() => void handleDownload(d.source)}
            disabled={downloading}
            style={{
              flex: "1 1 0",
              textAlign: "center",
              padding: "9px 12px", borderRadius: 8, cursor: downloading ? "wait" : "pointer",
              border: "1px solid #E5E7EB", background: "white",
              fontSize: 12, fontWeight: 700, color: "#111827",
            }}
          >
            ⬇ {d.label}
          </button>
        ))}
      </div>

      {/* Actions */}
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 14 }}>
        <Button
          variant="secondary"
          onClick={() => fileInputRef.current?.click()}
          disabled={busy}
          style={{ minWidth: 180 }}
        >
          {stage === "checking" ? "Checking…" : "⬆ Upload Edited Template"}
        </Button>

        <Button
          variant="secondary"
          onClick={handleReset}
          disabled={resetting || busy}
          style={{ minWidth: 180, color: '#B91C1C', borderColor: '#FECACA', background: '#FFF5F5' }}
        >
          {resetting ? "Resetting…" : "↩ Reset to Default Template"}
        </Button>

        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx"
          style={{ display: "none" }}
          onChange={handleFileChange}
          onInput={handleFileChange}
        />
      </div>

      {/* Review stage — findings, diff, confirm/cancel */}
      {review && (
        <div
          style={{
            border: `1px solid ${review.ok ? "#C7D2FE" : COLORS.error.border}`,
            borderRadius: 10,
            padding: "14px 16px",
            marginBottom: 14,
            background: "white",
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", marginBottom: 2 }}>
            {review.ok ? "Ready to apply" : "This file can’t be applied yet"}
          </div>
          <div style={{ fontSize: 12, color: "#6B7280", marginBottom: 12 }}>
            {review.filename}
            {review.ok
              ? " — nothing has been saved yet. Review the changes below, then confirm."
              : " — nothing was changed. Fix the problems below and upload again."}
          </div>

          <FindingsPanel findings={review.findings} />

          {review.ok && <DiffPanel result={review} />}

          {review.ok && !review.diff?.any_change && (
            <div style={{ fontSize: 12, color: "#6B7280", marginBottom: 12 }}>
              Applying will leave the template exactly as it is now.
            </div>
          )}

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {review.ok && (
              <Button onClick={handleApply} disabled={stage === "applying"} style={{ minWidth: 150 }}>
                {stage === "applying" ? "Applying…" : "✓ Apply Changes"}
              </Button>
            )}
            <Button
              variant="secondary"
              onClick={() => { setReview(null); setStage("idle"); }}
              disabled={stage === "applying"}
            >
              Cancel
            </Button>
            {!review.ok && (
              <Button variant="secondary" onClick={() => fileInputRef.current?.click()}>
                Choose a different file
              </Button>
            )}
          </div>
        </div>
      )}

      {storageWarning && (
        <div
          style={{
            fontSize: 12,
            color: "#92400E",
            background: "#FFFBEB",
            border: "1px solid #FCD34D",
            borderRadius: 6,
            padding: "8px 10px",
            marginBottom: 10,
            lineHeight: 1.5,
          }}
        >
          <strong>⚠ Ephemeral storage:</strong> Uploaded templates are saved in SQLite and will be
          lost when the server restarts or is redeployed. Contact your admin to configure a
          persistent PostgreSQL database (<code>DATABASE_URL</code> env var).
        </div>
      )}

      {lastResult && (
        <div
          style={{
            fontSize: 12,
            color: COLORS.ok.fg,
            background: COLORS.ok.bg,
            border: `1px solid ${COLORS.ok.border}`,
            borderRadius: 6,
            padding: "6px 10px",
            marginBottom: 10,
          }}
        >
          ✓ {lastResult}
        </div>
      )}

      {/* What's in the file */}
      <div style={{ fontSize: 12, color: "#9CA3AF", lineHeight: 1.6 }}>
        <strong style={{ color: "#6B7280" }}>The Excel file contains:</strong>
        <ul style={{ margin: "4px 0 0 16px", padding: 0 }}>
          <li>4 template tabs — one per mode/shift (Arrival Morning, Arrival Night, Departure Morning, Departure Night)</li>
          <li>2 counter tabs — define labels and motor status for Arrival and Departure counters</li>
          <li>Instructions tab — explains how to edit (read before editing)</li>
        </ul>
        <div style={{ marginTop: 6 }}>
          <strong style={{ color: "#6B7280" }}>Editing tips:</strong> Write a counter label only at the{" "}
          <em>start</em> of each working period. Leave the remaining slots in that period blank — they
          carry the counter forward automatically. Type <code>#</code> to mark a break slot. Column A
          (SN) identifies the officer, so rows can be sorted safely. Time columns are fixed at
          48 × 15 min and cannot be changed.
        </div>
      </div>

      <SnapshotList snapshots={snapshots} onRollback={handleRollback} busy={busy} />
    </div>
  );
}
